/**
 * Ad Units API Service
 * Handles all ad unit-related API calls with role-based routing
 *
 * Users:  /api/v1/campaigns/:campaign_id/ad_units
 * Admins: /api/v1/administrator/campaigns/:campaign_id/ad_units
 */

import { apiClient } from './client'
import { getStoredRole } from '../../utils/token'

export interface FramePreview {
  size: string
  size_key: string
  url: string
}

export interface StaticPreviewUrl {
  size: string
  size_key: string
  url: string
}

export interface PreviewUrls {
  base: string
  frame_previews?: FramePreview[]
  static_preview_urls?: StaticPreviewUrl[]
}

export interface AdUnit {
  id: number
  ad_unit_id: number
  campaign_id: number
  content_type: string
  status?: string
  creative_manifest?: any[]
  created_at: string
  updated_at: string
  name?: string
  sizes?: string[]
  preview_url?: string
  preview_urls?: PreviewUrls
  campaign_name?: string
  is_flip?: boolean
  auto_optimization?: boolean
  auto_rotate_highlight?: boolean
  auto_rotate?: boolean
  sequence?: boolean
  ad_type?: string
  sponsored_label?: string
  has_utm?: boolean
  smart_macros?: boolean
  smart_tag?: boolean
  conversions_tracking?: boolean | string
}

interface AdUnitsListResponse {
  ad_units: AdUnit[]
  meta?: {
    current_page: number
    total_pages: number
    total_count: number
  }
}

interface AdUnitResponse {
  ad_unit: AdUnit
}

export interface PublishedTag {
  content: string
  ad_unit_file_size: number | null
  size: string
}

export interface PublishTagsResponse {
  js_tags: PublishedTag[]
  network: string
  has_variants_with_names: boolean
}

export const NETWORK_OPTIONS = [
  { key: 'dcm', name: 'Google Campaign Manager' },
  { key: 'dbm', name: 'DV360' },
  { key: 'dfp', name: 'Google Ad Manager' },
  { key: 'gdn', name: 'Google Ads' },
  { key: 'app_nexus', name: 'Xandr' },
  { key: 'oath_platform', name: 'Yahoo' },
  { key: 'html', name: 'HTML' },
  { key: 'the_trade_desk', name: 'The Trade Desk' },
  { key: 'adform', name: 'Adform' },
  { key: 'mediamath', name: 'MediaMath' },
  { key: 'amobee', name: 'Nexxen' },
  { key: 'centro', name: 'Basis' },
  { key: 'quancast', name: 'Quantcast' },
  { key: 'knorex', name: 'Knorex' },
  { key: 'zemanta', name: 'Zemanta' },
  { key: 'stackadapt', name: 'StackAdapt' },
  { key: 'illumin', name: 'Illumin' },
  { key: 'mediasmart', name: 'Mediasmart' },
  { key: 'silverpush', name: 'SilverPush' },
  { key: 'infinitum', name: 'Infinitum' },
  { key: 'amadeus', name: 'Amadeus' },
  { key: 'hivestack', name: 'Hivestack' },
  { key: 'sqreem', name: 'Sqreem' },
  { key: 'freakout', name: 'Red Freakout' },
  { key: 'sabio', name: 'Sabio' },
] as const

export type NetworkKey = typeof NETWORK_OPTIONS[number]['key']

export interface AdUnitsQueryParams {
  page?: number
  per_page?: number
}

function getAdUnitsBasePath(campaignId: number): string {
  const role = getStoredRole()
  const prefix = role === 'admin' ? '/administrator/campaigns' : '/campaigns'
  return `${prefix}/${campaignId}/ad_units`
}

function buildQueryString(params: AdUnitsQueryParams): string {
  const queryParams = new URLSearchParams()
  if (params.page) queryParams.append('page', params.page.toString())
  if (params.per_page) queryParams.append('per_page', params.per_page.toString())
  const queryString = queryParams.toString()
  return queryString ? `?${queryString}` : ''
}

export const adUnitsApi = {
  getAdUnits: async (
    campaignId: number,
    params: AdUnitsQueryParams = {}
  ): Promise<{ adUnits: AdUnit[]; meta?: AdUnitsListResponse['meta'] }> => {
    const queryString = buildQueryString(params)
    const response = await apiClient.get<AdUnitsListResponse>(
      `${getAdUnitsBasePath(campaignId)}${queryString}`,
      { skipRoleRouting: true }
    )
    return {
      adUnits: response.ad_units || [],
      meta: response.meta
    }
  },

  getAdUnit: async (campaignId: number, adUnitId: number): Promise<AdUnit> => {
    const response = await apiClient.get<AdUnitResponse>(
      `${getAdUnitsBasePath(campaignId)}/${adUnitId}`,
      { skipRoleRouting: true }
    )
    return response.ad_unit
  },

  createAdUnit: async (campaignId: number, data: Record<string, any>): Promise<AdUnit> => {
    const response = await apiClient.post<AdUnitResponse>(
      getAdUnitsBasePath(campaignId),
      { ad_unit: data },
      { skipRoleRouting: true }
    )
    return response.ad_unit
  },

  updateAdUnit: async (campaignId: number, adUnitId: number, data: Record<string, any>): Promise<AdUnit> => {
    const response = await apiClient.patch<AdUnitResponse>(
      `${getAdUnitsBasePath(campaignId)}/${adUnitId}`,
      { ad_unit: data },
      { skipRoleRouting: true }
    )
    return response.ad_unit
  },

  deleteAdUnit: async (campaignId: number, adUnitId: number): Promise<void> => {
    await apiClient.delete(
      `${getAdUnitsBasePath(campaignId)}/${adUnitId}`,
      { skipRoleRouting: true }
    )
  },

  copyAdUnit: async (campaignId: number, adUnitId: number): Promise<AdUnit> => {
    const response = await apiClient.get<AdUnitResponse>(
      `${getAdUnitsBasePath(campaignId)}/${adUnitId}/copy`,
      { skipRoleRouting: true }
    )
    return response.ad_unit
  },

  publishTags: async (campaignId: number, adUnitId: number, network: NetworkKey): Promise<PublishTagsResponse> => {
    const response = await apiClient.get<PublishTagsResponse>(
      `${getAdUnitsBasePath(campaignId)}/${adUnitId}/publish?network=${network}`,
      { skipRoleRouting: true }
    )
    return response
  },
}
